/**
 * LinePointHandles Component
 *
 * Renders draggable point handles at each line point for individual point editing.
 * Handles are INVISIBLE by default and appear only on HOVER to avoid obscuring point numbers.
 *
 * Features:
 * - Draggable markers at each line point
 * - Invisible by default, visible on hover (smooth fade-in)
 * - Real-time line update while dragging
 * - Z-index above line to ensure clickability
 *
 * @module components/map/GeometryRenderer/LineRenderer/LinePointHandles
 */

import React, { memo, useState, useCallback, useMemo } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { Trash2 } from 'lucide-react';
import { useLinePointDrag } from '../../../../hooks/useLinePointDrag';
import { useDeviceLayout } from '../../../../hooks/useDeviceLayout';
import { useGeoMarkupStore } from '../../../../store';
import { AppTooltip } from '../../../ui/AppTooltip';
import { GeometryType } from '../../../../types/geometry';
import type { TaskGeometry, Coordinates } from '../../../../types';
import { calculateLineCenter, deleteVertexWithCascade } from '../../../../utils/geometry/lineHelpers';
import { MIN_POINTS_TO_CONNECT } from '../../../../constants/geometryRendering';
import {
  DELETE_BUTTON_SIZE,
  DELETE_BUTTON_ICON_SIZE,
  DELETE_BUTTON_POINT_OFFSET_X,
  DELETE_BUTTON_POINT_OFFSET_Y,
} from '../../../../constants/ui';
import classes from './LinePointHandles.module.css';

/**
 * Props for LinePointHandles component
 */
export interface LinePointHandlesProps {
  /** The line geometry containing points to render */
  geometry: TaskGeometry;
  /** Whether drag mode is enabled */
  isDraggable: boolean;
}

/**
 * LinePointHandles Component
 *
 * Renders interactive draggable handles at each line point
 * Handles appear only on hover to avoid obscuring point numbers
 */
export const LinePointHandles = memo(function LinePointHandles({
  geometry,
  isDraggable,
}: LinePointHandlesProps) {
  // Device capabilities - check if device has hover
  const { hasHover } = useDeviceLayout();

  // Memoize linePoints to prevent creating new array reference on every render
  const linePoints = useMemo(() => geometry.linePoints || [], [geometry.linePoints]);

  // Hook for handling individual point drag
  const { handlePointDragStart, handlePointDrag, handlePointDragEnd } = useLinePointDrag(
    geometry.id
  );

  // Track which point is currently hovered
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Track which point is currently being dragged (prevents flickering)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  // Selection mode state from Zustand
  const activeTool = useGeoMarkupStore((s) => s.activeTool);
  const selectedGeometryId = useGeoMarkupStore((s) => s.selectedGeometryId);
  const selectedVertexIndex = useGeoMarkupStore((s) => s.selectedVertexIndex);
  const selectVertex = useGeoMarkupStore((s) => s.selectVertex);
  const deselectVertex = useGeoMarkupStore((s) => s.deselectVertex);
  const updateGeometry = useGeoMarkupStore((s) => s.updateGeometry);
  const removeGeometry = useGeoMarkupStore((s) => s.removeGeometry);
  const activeParentIndex = useGeoMarkupStore((s) => s.activeParentIndex);
  const setActiveParentIndex = useGeoMarkupStore((s) => s.setActiveParentIndex);

  // Check if this geometry is selected in Selection mode
  const isGeometrySelectedInSelectionMode =
    selectedGeometryId === geometry.id && activeTool === GeometryType.Selection;

  /**
   * Handle vertex deletion
   *
   * Deletes the selected vertex using child reconnection strategy.
   * If less than 2 unique points remain, deletes the entire line geometry.
   */
  const handleDeleteVertex = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent map click

      // deleteVertexWithCascade returns null if line should be deleted (< 2 unique points)
      const result = deleteVertexWithCascade(
        index,
        linePoints,
        geometry.parentIndices || []
      );

      if (result === null) {
        // Delete entire line geometry (less than 2 unique points)
        removeGeometry(geometry.id);
      } else {
        // Update line with vertex removed
        const newCenter = calculateLineCenter(result.linePoints);
        updateGeometry(geometry.id, {
          linePoints: result.linePoints,
          parentIndices: result.parentIndices,
          calculatedCenter: newCenter || undefined,
        });
      }

      // Clear vertex selection
      deselectVertex();
    },
    [linePoints, geometry.id, geometry.parentIndices, removeGeometry, updateGeometry, deselectVertex]
  );

  /**
   * Handle vertex click
   *
   * Point Connection (Drawing/Edit modes):
   * - Clicking any existing point handle connects last point to that point
   * - Works when line has >= MIN_POINTS_TO_CONNECT points
   * - Example: 1→2→3, click point 1 → 1→2→3→1 (closed triangle)
   * - Example: 1→2→3→1→4→5, click point 3 → 1→2→3→1→4→5→3 (diagonal)
   *
   * Vertex Selection (Selection mode):
   * - Selects the clicked vertex for editing
   */
  const handleVertexClick = useCallback(
    (index: number, e: React.MouseEvent) => {
      // FEATURE: Point Connection with Branching Support (Drawing/Edit modes)
      // Clicking existing point handle:
      // - If edge already exists → Set activeParentIndex (branch from that point)
      // - If edge doesn't exist → Add point with parent relationship
      // Works in Drawing mode (activeTool === Line) or Edit mode (isDraggable)
      // Does NOT work in Selection mode (vertex selection has priority)
      const isDrawingOrEditMode = isDraggable && activeTool !== GeometryType.Selection;

      if (isDrawingOrEditMode && linePoints.length >= MIN_POINTS_TO_CONNECT) {
        e.stopPropagation(); // Prevent map click (which would add point on map)

        const parentIndices = geometry.parentIndices || [];
        const lastIndex = linePoints.length - 1;

        // Determine source point for edge checking:
        // - If activeParentIndex is set → use it (branching mode, green point is source)
        // - Otherwise → use last point (normal sequential drawing)
        const sourceIndex = activeParentIndex ?? lastIndex;
        const sourcePoint = linePoints[sourceIndex];
        const targetPoint = linePoints[index];

        // Safety check: ensure both points exist
        if (!targetPoint || !sourcePoint) {
          return;
        }

        // 1. BLOCK: Clicking already active vertex (no need to switch to same vertex)
        // This allows switching between points even if they share coordinates (e.g., in closed loops)
        if (index === activeParentIndex) {
          return; // Point is already active (green), no action needed
        }

        // 2. CHECK: Does edge "sourcePoint → targetPoint" already exist in line?
        // Find current parent of last point (for direct edge check fallback)
        const currentParentIndex = parentIndices[lastIndex];

        // Check if edge already exists:
        // Option A: Direct edge check (if source is last point and its parent is target)
        const directEdgeExists = sourceIndex === lastIndex && currentParentIndex === index;

        // Option B: Search all edges for sourcePoint ↔ targetPoint connection (bidirectional)
        // Lines are undirected, so A→B is the same as B→A
        const edgeExistsAnywhere = parentIndices.some((parentIdx, pointIdx) => {
          if (parentIdx === null || pointIdx >= linePoints.length) return false;

          const point = linePoints[pointIdx];
          const parentPoint = linePoints[parentIdx];

          if (!point || !parentPoint) return false;

          // Check if this edge matches sourcePoint → targetPoint OR targetPoint → sourcePoint
          return (
            (parentPoint.lat === sourcePoint.lat &&
             parentPoint.lng === sourcePoint.lng &&
             point.lat === targetPoint.lat &&
             point.lng === targetPoint.lng) ||
            (parentPoint.lat === targetPoint.lat &&
             parentPoint.lng === targetPoint.lng &&
             point.lat === sourcePoint.lat &&
             point.lng === sourcePoint.lng)
          );
        });

        if (directEdgeExists || edgeExistsAnywhere) {
          // 3. BRANCH: Edge already exists → Jump to target point (next draws from there)
          setActiveParentIndex(index);
          return; // Don't add point, just set branch point
        }

        // 4. ADD POINT: Edge doesn't exist → Add point with parent relationship
        const updatedPoints: Coordinates[] = [...linePoints, targetPoint];

        // Build or extend parentIndices array
        const updatedParents: (number | null)[] = parentIndices.length > 0
          ? [...parentIndices, lastIndex] // Extend existing array
          : [
              ...new Array(linePoints.length).fill(null).map((_, i) => i === 0 ? null : i - 1),
              lastIndex
            ]; // Create array from scratch (backward compat)

        // Ensure first point is always root (null parent)
        if (updatedParents.length > 0) {
          updatedParents[0] = null;
        }

        // Recalculate center (average of all points)
        const newCenter = calculateLineCenter(updatedPoints);

        // Update geometry in Zustand store
        updateGeometry(geometry.id, {
          linePoints: updatedPoints,
          parentIndices: updatedParents,
          calculatedCenter: newCenter || undefined,
        });

        // Set original point as active (green highlight)
        // When connecting to existing point, we add duplicate but UI shows only first occurrence
        // So activeParentIndex must point to original index (not duplicate) for correct highlighting
        // Example: Line 1→2→3→4, click point 1 (index=0) → adds duplicate at index 4
        //          → uniquePoints shows only [0,1,2,3] → set activeParentIndex=0 (not 4)
        setActiveParentIndex(index);

        return; // Done - don't proceed to Selection mode logic
      }

      // ORIGINAL: Vertex Selection (Selection mode only)
      if (activeTool === GeometryType.Selection && isGeometrySelectedInSelectionMode) {
        e.stopPropagation(); // Prevent selecting the entire line
        selectVertex(index);
      }
    },
    [
      activeTool,
      isDraggable,
      linePoints,
      geometry.id,
      geometry.parentIndices,
      isGeometrySelectedInSelectionMode,
      selectVertex,
      updateGeometry,
      activeParentIndex,
      setActiveParentIndex,
    ]
  );

  /**
   * Group line points by coordinates
   * Maps each unique coordinate to array of point indices at that location
   *
   * This prevents drag & drop bugs where duplicate points (from connections like 1→2→3→1)
   * create multiple overlapping drag handles at the same location.
   *
   * @example
   * ```typescript
   * // Line 1→2→3→1 creates linePoints: [p1, p2, p3, p1_duplicate]
   * // pointGroups result:
   * // - "50.0,20.0" → [0, 3] (point 1 appears at indices 0 and 3)
   * // - "50.1,20.1" → [1]    (point 2 at index 1)
   * // - "50.2,20.2" → [2]    (point 3 at index 2)
   * ```
   */
  const pointGroups = useMemo(() => {
    const groups = new Map<string, number[]>();

    linePoints.forEach((point, index) => {
      const key = `${point.lat},${point.lng}`;
      const existing = groups.get(key) || [];
      groups.set(key, [...existing, index]);
    });

    return groups;
  }, [linePoints]);

  /**
   * Get unique points (first occurrence only) for drag handle rendering
   * Prevents multiple overlapping drag handles at same coordinates
   *
   * When dragging a point with duplicates, ALL instances at that coordinate
   * will move together (synchronized drag).
   *
   * @example
   * ```typescript
   * // Line 1→2→3→1
   * // linePoints: [p1, p2, p3, p1_dup] (4 points)
   * // uniquePoints: [p1, p2, p3] (3 points) - only first occurrences
   * // Result: Only 3 drag handles rendered (matching numbered markers)
   * ```
   */
  const uniquePoints = useMemo(() => {
    const seen = new Set<string>();
    return linePoints
      .map((point, index) => ({ point, index }))
      .filter(({ point }) => {
        const key = `${point.lat},${point.lng}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [linePoints]);

  // Check if point connection is enabled (Drawing/Edit mode with >= MIN_POINTS_TO_CONNECT)
  const isConnectionEnabled =
    isDraggable &&
    activeTool !== GeometryType.Selection &&
    linePoints.length >= MIN_POINTS_TO_CONNECT;

  // Render points when: (draggable in Drawing mode) OR (selected in Selection mode)
  // Don't render if: no points OR (not draggable AND not selected)
  if (linePoints.length === 0 || (!isDraggable && !isGeometrySelectedInSelectionMode)) {
    return null;
  }

  return (
    <>
      {uniquePoints.map(({ point, index }) => {
        // Get all indices at this coordinate (for synchronized drag of duplicates)
        const key = `${point.lat},${point.lng}`;
        const allIndices = pointGroups.get(key) || [index];
        // Show handle when: hovered OR currently being dragged OR selected vertex OR line selected
        const isSelected =
          isGeometrySelectedInSelectionMode && selectedVertexIndex === index;
        const isVisible =
          hoveredIndex === index ||
          draggingIndex === index ||
          isSelected ||
          isGeometrySelectedInSelectionMode; // Show all points when line is selected

        return (
          <Marker
            key={`point-handle-${geometry.id}-${index}`}
            longitude={point.lng}
            latitude={point.lat}
            anchor="center"
            draggable={isDraggable && activeTool !== GeometryType.Selection}
            onDragStart={(e) => {
              setDraggingIndex(index); // Mark as dragging (prevents flickering)
              handlePointDragStart(e, allIndices); // ✅ Pass ALL indices for synchronized drag
            }}
            onDrag={(e) => handlePointDrag(e, allIndices)} // ✅ Update ALL duplicates
            onDragEnd={(e) => {
              handlePointDragEnd(e, allIndices); // ✅ Finalize ALL duplicates
              setDraggingIndex(null); // Clear dragging state
            }}
          >
            <div
              className={`${classes.pointHandle} ${isVisible ? classes.visible : classes.hidden} ${isSelected ? classes.selected : ''} ${isConnectionEnabled ? classes.connectable : ''}`}
              data-point-index={index}
              data-selection-mode={activeTool === GeometryType.Selection ? 'true' : undefined}
              onMouseEnter={() => hasHover && setHoveredIndex(index)}
              onMouseLeave={() => hasHover && setHoveredIndex(null)}
              onClick={(e) => handleVertexClick(index, e)}
            >
              {/* Visual handle - small circle, visible only on hover or when selected */}
              <div className={classes.handleInner} />

              {/* Delete button - only shown when vertex is selected in Selection mode */}
              {isSelected && (
                <AppTooltip labelKey="tooltips.selection.deleteVertex" position="top">
                  <button
                    className={classes.deleteButton}
                    onClick={(e) => handleDeleteVertex(index, e)}
                    onMouseEnter={() => hasHover && setHoveredIndex(index)}
                    style={{
                      '--button-size': `${DELETE_BUTTON_SIZE}px`,
                      '--icon-size': `${DELETE_BUTTON_ICON_SIZE}px`,
                      '--offset-x': `${DELETE_BUTTON_POINT_OFFSET_X}px`,
                      '--offset-y': `${DELETE_BUTTON_POINT_OFFSET_Y}px`,
                    } as React.CSSProperties}
                    aria-label={`Delete point ${index + 1}`}
                  >
                    <Trash2 size={DELETE_BUTTON_ICON_SIZE} />
                  </button>
                </AppTooltip>
              )}
            </div>
          </Marker>
        );
      })}
    </>
  );
});
